import { useState, useEffect, useContext } from "react";
import "./questionnaireAppBarSmall.scss";
import { useParams, useNavigate } from "react-router-dom";
import { notification, Modal } from "antd";
import axios from "axios";
// import { Flex, Progress } from "antd";

// context
import { AuthContext } from "../../contexts/authContext";
import { QuestionnaireContext } from "../../contexts/questionnaireContext";

// icon
import {
  upIcon,
  downIcon,
  userAddIcon,
  userIcon,
  closeIcon,
  signalIcon,
} from "../../constants/icons";

// ui-component
import CustomSelect from "../UI/CustomSelect/CustomSelect";
import CollabInviteModal from "../CollabInviteModal/CollabInviteModal";

type NotificationType = "success" | "info" | "warning" | "error";

const API_URL = "/api/v1";

const QuestionnaireAppBarSmall = ({
  toggleDrawer,
}: {
  toggleDrawer: () => void;
}) => {
  const {setUser} = useContext(AuthContext)
  const navigate = useNavigate();
  const { id } = useParams();
  const {
    evalScope,
    fetchEvaluationInfo,
    questionCount,
    selectedOptionCount,
    actionCount,
    handleSaveEvaluationResponse,
    removeCollaborator,
  } = useContext(QuestionnaireContext);
  const [api, contextHolder] = notification.useNotification();
  const [expand, setExpand] = useState<boolean>(false);
  const [collabModal, setCollabModal] = useState<boolean>(false);
  const [collabEvalId, setCollabEvalId] = useState<string>("");
  const [collab, setCollab] = useState<{
    cid: string;
    name: string;
    eid: string;
  }>({
    eid: "",
    cid: "",
    name: "",
  });
  const [removeCollabModal, setRemoveCollabModal] = useState<boolean>(false);

  const opNotification = (
    type: NotificationType,
    message: string,
    descripton: string
  ) => {
    api[type]({
      message: message,
      description: descripton,
    });
  };

  const navigateToBreakdown = async () => {
    const res = await handleSaveEvaluationResponse();
    if (res.success === true) {
      navigate(`/dashboard/evaluation/breakdown/${id}`);
    }
    if (res.errors && res.errors.length > 0) {
      opNotification(
        "warning",
        "Missing Required Input",
        "Please fill all the inputs marked with *"
      );
    }
  };

  const handleRemoveCollaborator = async () => {
    try {
      const res = await axios.delete(
        `${API_URL}/evaluation/remove/collaborator/${collab.eid}/${collab.cid}`
      );
      if (res.status === 200) {
        if (res.data.success === false) {
          opNotification(
            "error",
            "Unauthorized Action",
            "Not authorized to remove collaborator."
          );
          setCollab({
            eid: "",
            cid: "",
            name: "",
          });
          setRemoveCollabModal(false);
        } else {
          removeCollaborator(collab.cid);
          setCollab({
            eid: "",
            cid: "",
            name: "",
          });
          setRemoveCollabModal(false);

          opNotification(
            "success",
            "Collaborator Removed",
            "Collaborator removed successfully."
          );
        }
      }
    } catch (error:any) {
      console.log("Error removing collaborator: ", error);
      if(error.response){
        const {status} = error.response;
        if(status === 401 || status === 500){
          setUser(null)
        }
      }
    }
  };

  useEffect(() => {
    async function fetchInfo() {
      const response = await fetchEvaluationInfo(id);
      if (response === false) {
        navigate("/dashboard/evaluation");
      }
    }
    fetchInfo();
    // eslint-disable-next-line
  }, []);

  return (
    <div className="questionnaireAppBarSmall">
      {contextHolder}
      <div className="badgeContainer">
        <span
          className={`approachBadge ${
            evalScope.evaluation_type === "guided-approach" && "gradient"
          }`}
        >
          {evalScope.evaluation_type === "guided-approach"
            ? "Guided approach questionnaire"
            : "User Driven questionnaire"}
        </span>
        <span
          onClick={() => {
            setExpand((prev) => !prev);
          }}
        >
          {expand ? upIcon : downIcon}
        </span>
      </div>
      {expand && (
        <div className="expandComponentContainer">
          <span className="title">{evalScope.name}</span>
          <div className="collab">
            <span>Collaborators:</span>
            <span
              onClick={() => {
                setCollabEvalId(evalScope._id);
                setCollabModal(true);
              }}
            >
              {userAddIcon}
            </span>
            {evalScope.collaborators.length > 2 ? (
              <span className="collabBadge">{userIcon} 2+...</span>
            ) : (
              evalScope.collaborators.map((colab: any, index: number) => (
                <span key={index} className="collabBadge">
                  {userIcon} {colab.name}
                  <span
                    onClick={()=>{
                      setCollab({
                        eid: evalScope._id,
                        cid: colab.cid,
                        name: colab.name
                      })
                      setRemoveCollabModal(true);
                    }}
                  >{closeIcon}</span>
                </span>
              ))
            )}
          </div>
          <div className="actionsText">
            <span>Actions to align to requirements:</span>
            <span className="count" onClick={toggleDrawer}>
              {actionCount} actions
            </span>
          </div>
          <div className="countBarContainer">
            <div className="countText">
              <span>Total Number of questions: {questionCount}</span>
            </div>
            <div className="barContainer">
              <div className="bar">
                <div
                  className="progressUI"
                  style={{ height: `${selectedOptionCount.yes.percent}%` }}
                >
                  {selectedOptionCount.yes.count}
                </div>
                <span className="text">Yes</span>
              </div>
              <div className="bar">
                <div
                  className="progressUI"
                  style={{ height: `${selectedOptionCount.no.percent}%` }}
                >
                  {selectedOptionCount.no.count}
                </div>
                <span className="text">No</span>
              </div>
              <div className="bar">
                <div
                  className="progressUI"
                  style={{ height: `${selectedOptionCount.na.percent}%` }}
                >
                  {selectedOptionCount.na.count}
                </div>
                <span className="text">N/A</span>
              </div>
              <div className="bar">
                <div
                  className="progressUI"
                  style={{ height: `${selectedOptionCount.tbd.percent}%` }}
                >
                  {selectedOptionCount.tbd.count}
                </div>
                <span className="text">TBD</span>
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="expandFooter">
        <div className="breakdownBtn" onClick={navigateToBreakdown}>
          <span>{signalIcon} See detailed breakdown</span>
        </div>
        <div className="dropdownContainer">
          <CustomSelect />
        </div>
      </div>
      <CollabInviteModal
        evaluationId={collabEvalId}
        collabModal={collabModal}
        setCollabModal={setCollabModal}
      />
      <Modal
        open={removeCollabModal}
        closable={false}
        centered
        onCancel={() => {
          setRemoveCollabModal(false);
        }}
        footer={null}
      >
        <div className="deleteEvaluationContainer">
          <span className="header">Remove Collaborator</span>
          <p>Do you want to remove {collab.name} from collaboration?</p>
          <div className="buttonHolder">
            <button
              onClick={() => {
                setCollab({ eid: "", cid: "", name: "" });
                setRemoveCollabModal(false);
              }}
            >
              Cancel
            </button>
            <button
              className="btnDelete"
              onClick={() => {
                handleRemoveCollaborator();
              }}
            >
              Remove
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default QuestionnaireAppBarSmall;
